// PreviewBanner.js
//
// Slim fixed-position banner that shows when this tab is in
// "preview as manager" mode (see previewSession.js). It's the user's
// only persistent indicator that the dashboard they're looking at
// belongs to a building they're impersonating — without it, a
// maintenance user could easily forget they're in preview and try to
// edit data as if they were the actual manager.
//
// The banner pushes the rest of the page down via padding-top on
// document.body so it never overlaps the manager dashboard's own
// fixed header / navbar.

import React, { useEffect, useState } from 'react';
import { isPreviewMode, getPreviewBuildingId, exitPreviewMode } from './previewSession';
import API_BASE_URL from './config/api';
import './PreviewBanner.css';

const BANNER_HEIGHT = 36;

export default function PreviewBanner() {
  const [active] = useState(() => isPreviewMode());
  const [buildingLabel, setBuildingLabel] = useState(() => getPreviewBuildingId() || '');

  // Push the rest of the page down so the banner never overlaps the
  // app's own headers. We restore the original padding when unmounting
  // OR when preview mode is exited.
  useEffect(() => {
    if (!active) return undefined;
    const body = document.body;
    const prev = body.style.paddingTop;
    const hadPreviewClass = body.classList.contains('dashboard-preview-active');
    const previousOffset = body.style.getPropertyValue('--dashboard-preview-offset');
    const previousOffsetPriority = body.style.getPropertyPriority('--dashboard-preview-offset');
    body.classList.add('dashboard-preview-active');
    body.style.setProperty('--dashboard-preview-offset', `${BANNER_HEIGHT}px`);
    body.style.paddingTop = `${BANNER_HEIGHT}px`;
    return () => {
      body.style.paddingTop = prev;
      if (!hadPreviewClass) body.classList.remove('dashboard-preview-active');
      if (previousOffset) body.style.setProperty('--dashboard-preview-offset', previousOffset, previousOffsetPriority);
      else body.style.removeProperty('--dashboard-preview-offset');
    };
  }, [active]);

  // Pretty up the building label — start with the ID, upgrade to the
  // building name once /api/buildings comes back. Best-effort; we
  // intentionally fall back to the raw ID on any error.
  useEffect(() => {
    if (!active) return;
    const bid = getPreviewBuildingId();
    if (!bid) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/buildings`);
        if (!r.ok) return;
        const data = await r.json();
        const list = Array.isArray(data) ? data : data?.buildings || [];
        const hit = list.find(
          (b) =>
            String(b.buildingId) === String(bid) ||
            String(b.objectId) === String(bid),
        );
        if (!cancelled && hit) {
          const name = hit.building_name || hit.name || hit.address || bid;
          setBuildingLabel(name);
        }
      } catch (_) {
        /* keep raw id as label */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);

  if (!active) return null;

  return (
    <div
      className="preview-banner"
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: BANNER_HEIGHT,
        zIndex: 100000,
        background: '#1F3A68',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 14px',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif',
        fontSize: 13,
        fontWeight: 500,
        letterSpacing: '0.01em',
        boxShadow: '0 1px 4px rgba(20, 30, 60, .25)',
      }}
    >
      <div className="preview-banner-label" style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <span
          aria-hidden
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#fbbf24',
            flexShrink: 0,
          }}
        />
        <span className="preview-banner-intro" style={{ whiteSpace: 'nowrap' }}>Previewing as manager</span>
        {buildingLabel ? (
          <span
            className="preview-banner-building"
            style={{
              opacity: 0.85,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={buildingLabel}
          >
            · {buildingLabel}
          </span>
        ) : null}
      </div>
      <button
        className="preview-banner-exit"
        type="button"
        onClick={exitPreviewMode}
        style={{
          background: 'rgba(255,255,255,.12)',
          color: '#fff',
          border: '1px solid rgba(255,255,255,.25)',
          borderRadius: 6,
          padding: '4px 12px',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          letterSpacing: '0.02em',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,.22)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,.12)';
        }}
      >
        Exit preview
      </button>
    </div>
  );
}
